import { i18n } from "../../translate/i18n";

import React, { useState, useEffect, useContext } from "react";
import { Helmet } from "react-helmet";

import { 
  Button, 
  TextField, 
  Typography, 
  IconButton, 
  InputAdornment, 
  Paper,
  CssBaseline,
  Link,
  InputLabel,
  OutlinedInput,
  FormControl,
  FormControlLabel,
  Checkbox
} from "@material-ui/core";

import { makeStyles } from "@material-ui/core/styles";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import EmailIcon from "@material-ui/icons/Email";
import LockIcon from "@material-ui/icons/Lock";

import logo from "../../assets/logo.png";
import useSettings from "../../hooks/useSettings";
import { getBackendUrl } from "../../config";
import { openApi } from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100vh",
    width: "100vw",
    overflow: "hidden",
    position: "relative",
    background: "linear-gradient(135deg, #001b3d 0%, #004e92 100%)",
    fontFamily: "'Inter', sans-serif",
  },

  // 🔹 Fundo Animado (Efeito Mesh)
  animatedBg: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
    opacity: 0.6,
    background: "radial-gradient(circle at 20% 30%, #4D9CFF 0%, transparent 40%), radial-gradient(circle at 80% 70%, #001b3d 0%, transparent 40%)",
    filter: "blur(80px)",
    animation: "$meshMove 15s ease-in-out infinite alternate",
  },

  "@keyframes meshMove": {
    "0%": { transform: "translate(0, 0) scale(1)" },
    "100%": { transform: "translate(5%, 5%) scale(1.1)" },
  },

  paper: {
    zIndex: 1,
    width: "100%",
    maxWidth: "400px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "40px 32px",
    borderRadius: "24px",
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.3)",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
    [theme.breakpoints.down("sm")]: {
      padding: "32px 24px",
      maxWidth: "90%",
    },
  },

  logoContainer: {
    marginBottom: "16px",
    textAlign: "center",
    "& img": {
      maxWidth: "180px",
      height: "auto",
      filter: "drop-shadow(0 4px 6px rgba(0,0,0,0.05))",
    },
  },

  form: {
    width: "100%",
    marginTop: theme.spacing(1),
  },

  textField: {
    marginBottom: theme.spacing(1.5),
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      transition: "all 0.2s ease",
      backgroundColor: "#f8fafc",
      "&:hover": {
        backgroundColor: "#f1f5f9",
      },
      "&.Mui-focused": {
        backgroundColor: "#fff",
        boxShadow: "0 0 0 4px rgba(0, 78, 146, 0.1)",
      }
    },
  },

  submit: {
    margin: theme.spacing(2, 0, 1),
    padding: "12px",
    borderRadius: "12px",
    fontSize: "1rem",
    fontWeight: 700,
    textTransform: "none",
    background: "linear-gradient(135deg, #001b3d 0%, #004e92 100%)",
    boxShadow: "0 10px 15px -3px rgba(0, 27, 61, 0.3)",
    color: "#fff",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 15px 25px -5px rgba(0, 27, 61, 0.4)",
    },
  },

  footer: {
    marginTop: "8px",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
    "& a": {
      color: "#64748b",
      fontSize: "0.85rem",
      textDecoration: "none",
      transition: "color 0.2s",
      "&:hover": {
        color: "#004e92",
      },
    },
  },

  whatsappButton: {
    position: "fixed",
    bottom: 30,
    right: 30,
    backgroundColor: "#25d366",
    color: "#fff",
    borderRadius: "50%",
    width: 60,
    height: 60,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 10px 25px rgba(37, 211, 102, 0.3)",
    cursor: "pointer",
    zIndex: 1000,
    transition: "all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
    "&:hover": {
      transform: "scale(1.1) rotate(5deg)",
      boxShadow: "0 15px 30px rgba(37, 211, 102, 0.4)",
    },
  },
}));

const Login = () => {
  const classes = useStyles();
  const { handleLogin } = useContext(AuthContext);
  const { getPublicSetting } = useSettings();

  const [user, setUser] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [appName, setAppName] = useState("AtendeChat Pro");
  const [userCreationEnabled, setUserCreationEnabled] = useState(false);
  const [branding, setBranding] = useState({
    loginLogo: "",
    loginWhatsapp: "",
  });

  useEffect(() => {
    document.body.classList.add("login-page");
    let isMounted = true;
    const fetchData = async () => {
      try {
        const { data: brandingData } = await openApi.get("/global-config/public-branding");
        if (isMounted) {
          setBranding({
            loginLogo: brandingData.loginLogo,
            loginWhatsapp: brandingData.loginWhatsapp,
          });
        }

        const creationStatus = await openApi.get("/settings/userCreation");
        if (isMounted) {
          setUserCreationEnabled(creationStatus.data.userCreation === "enabled");
        }

        const name = await getPublicSetting("appName");
        if (isMounted) {
          setAppName(name || "AtendeChat Pro");
        }
      } catch (err) {
        console.error("Erro ao carregar configurações:", err);
      }
    };
    fetchData();
    return () => {
      isMounted = false;
      document.body.classList.remove("login-page");
    };
  }, [getPublicSetting]);

  const handleChangeInput = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleLogin(user);
  };

  const brandingData = {
    loginLogo: branding.loginLogo ? (branding.loginLogo.startsWith("http") ? branding.loginLogo : `${getBackendUrl()}${branding.loginLogo}`) : logo,
  };

  return (
    <div className={classes.root}>
      <CssBaseline />
      <Helmet>
        <title>{appName}</title>
      </Helmet>
      
      <div className={classes.animatedBg} />
      
      <Paper className={classes.paper} elevation={0}>
        <div className={classes.logoContainer}>
          <img
            src={brandingData.loginLogo}
            alt="Logo"
            onError={(e) => {
              // Se a logo remota (branding) falhar em produção, cai para a logo
              // embutida no build — evita o ícone de imagem quebrada.
              if (e.target.src !== logo) {
                e.target.onerror = null;
                e.target.src = logo;
              }
            }}
          />
        </div>

        <Typography component="h1" variant="h5" style={{ fontWeight: 800, color: "#0f172a", marginBottom: "16px" }}>
          Login
        </Typography>

        <form className={classes.form} noValidate onSubmit={handleSubmit}>
          <TextField
            className={classes.textField}
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="email"
            label={i18n.t("login.form.email")}
            name="email"
            value={user.email}
            onChange={handleChangeInput}
            autoComplete="email"
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            className={classes.textField}
            variant="outlined"
            margin="normal"
            required
            fullWidth
            name="password"
            label={i18n.t("login.form.password")}
            type={showPassword ? "text" : "password"}
            id="password"
            autoComplete="current-password"
            value={user.password}
            onChange={handleChangeInput}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword((e) => !e)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <FormControlLabel
            control={<Checkbox value="remember" color="primary" />}
            label="Lembrar de mim"
            style={{ color: "#64748b" }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            className={classes.submit}
          >
            {i18n.t("login.buttons.submit")}
          </Button>
          
          <div className={classes.footer}>
            <Link href="#" variant="body2">
              Esqueceu sua senha?
            </Link>
          </div>
        </form>
      </Paper>

      {branding.loginWhatsapp && (
        <div
          className={classes.whatsappButton}
          onClick={() => window.open(branding.loginWhatsapp, "_blank")}
        >
          <WhatsAppIcon style={{ fontSize: 32 }} />
        </div>
      )}
    </div>
  );
};

export default Login;
